import { motion, AnimatePresence } from "framer-motion";

import { cn } from "@/lib/utils";
import { SubMenu } from "@/types/menu";

import { getMenuIcon, submenuVariants } from "../constants";
import { getBestMatchId } from "../helpers";

interface SidebarSubMenuProps {
  subMenus: SubMenu[];
  isOpen: boolean;
  currentPath: string;
  currentSearch: string;
  counts: Record<string, number>;
  onNavigate: (sub: SubMenu) => void;
}

export const SidebarSubMenu = ({
  subMenus,
  isOpen,
  currentPath,
  currentSearch,
  counts,
  onNavigate,
}: SidebarSubMenuProps) => {
  const sorted = subMenus.slice().sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
  const activeSubId = getBestMatchId(subMenus, currentPath, currentSearch);

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="submenu"
          variants={submenuVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className="my-1 ml-5 overflow-hidden border-l border-sidebar-border pl-4"
        >
          <div className="space-y-1 py-1">
            {sorted.map((sub) => {
              const SubIcon = getMenuIcon(sub.id);
              const isSubActive = sub.id === activeSubId;
              const subCount = counts[sub.id] ?? 0;

              return (
                <a
                  key={sub.id}
                  href={sub.url}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate(sub);
                  }}
                  className={cn(
                    "group/sub relative flex items-center justify-between rounded-md px-3 py-2 text-sm outline-none transition-colors",
                    isSubActive
                      ? "font-medium text-sidebar-accent-foreground"
                      : "text-sidebar-foreground/60 hover:bg-sidebar-accent/30 hover:text-sidebar-foreground",
                  )}
                >
                  {isSubActive && (
                    <motion.div
                      layoutId="sidebar-active-sub"
                      className="absolute inset-0 rounded-md bg-sidebar-accent"
                      transition={{ duration: 0.2, ease: "easeOut" }}
                    />
                  )}

                  <div className="relative z-10 flex items-center">
                    <SubIcon
                      className={cn(
                        "mr-3 h-4 w-4 transition-colors",
                        isSubActive
                          ? "text-primary"
                          : "text-sidebar-foreground/40 group-hover/sub:text-sidebar-foreground/80",
                      )}
                    />
                    <span className="truncate">{sub.nome}</span>
                  </div>

                  {subCount > 0 && (
                    <span className="relative z-10 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground shadow-sm">
                      {subCount > 9 ? "9+" : subCount}
                    </span>
                  )}
                </a>
              );
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
